import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, Upload, Trash2, FileText } from 'lucide-react';
import { Document, Pager } from '../../services/RAG';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getRAGFiles, uploadRAGFile, deleteRAGFile } from '../../services/RAG';

const RAGPage: React.FC = () => {
  const [files, setFiles] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pagination state
  const [currentPager, setCurrentPager] = useState<Pager<Document> | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageSize] = useState(10);

  const columns: ColumnDef<Document>[] = [
    {
      accessorFn: (row) => row.displayName ?? row.name,
      accessorKey: 'displayName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent"
          >
            Filename
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.getValue('displayName') || row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'sizeBytes',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent"
          >
            File Size
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const sizeBytes = row.getValue('sizeBytes') as string | undefined;
        return sizeBytes ? formatFileSize(sizeBytes) : 'N/A';
      },
    },
    {
      accessorKey: 'state',
      header: 'Status',
      cell: ({ row }) => {
        const state = row.getValue('state') as string | undefined;
        if (!state) return <Badge variant="secondary">Unknown</Badge>;

        const variant =
          state === 'STATE_ACTIVE'
            ? 'success'
            : state === 'STATE_PROCESSING'
              ? 'warning'
              : 'destructive';
        return <Badge variant={variant}>{state}</Badge>;
      },
    },
    {
      accessorKey: 'createTime',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent"
          >
            Uploaded At
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const createTime = row.getValue('createTime') as string | undefined;
        if (!createTime) return 'N/A';
        const date = new Date(createTime);
        return date.toLocaleString();
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const file = row.original;
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteClick(file)}
            className="h-auto p-0 hover:bg-transparent"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: files,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const pager = await getRAGFiles(pageSize);
      setFiles(pager.page);
      setCurrentPager(pager);
    } catch (error) {
      toast.error('Error loading files');
      console.error('Error fetching RAG files:', error);
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleLoadMore = async () => {
    if (currentPager?.hasNextPage()) {
      setLoadingMore(true);
      try {
        // Get the next page of documents
        const nextDocuments = await currentPager.nextPage();

        // Append new files to existing ones
        setFiles([...files, ...nextDocuments]);
        setCurrentPager(currentPager)
      } catch (error) {
        toast.error('Error loading more files');
        console.error('Error loading more files:', error);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadStatus('Starting upload...');

    try {
      await uploadRAGFile(file, (status) => {
        setUploadStatus(status);
      });
      toast.success(`File "${file.name}" uploaded successfully`);

      // Refresh the list from the beginning
      fetchFiles();
    } catch (error) {
      toast.error('Error uploading file');
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      setUploadStatus('');
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteClick = (file: Document) => {
    setFileToDelete(file);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete || !fileToDelete.name) return;

    setDeleting(true);
    try {
      await deleteRAGFile(fileToDelete.name);
      toast.success(`File "${fileToDelete.displayName || fileToDelete.name}" deleted successfully`);

      // Refresh the list from the beginning
      fetchFiles();
    } catch (error) {
      toast.error('Error deleting file');
      console.error('Error deleting file:', error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const formatFileSize = (sizeBytes: string): string => {
    const bytes = parseInt(sizeBytes, 10);
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="w-full space-y-4 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Knowledge Database</h1>
          <p className="text-muted-foreground">
            Manage your sources for AI-powered search and retrieval
          </p>
        </div>
        <Button onClick={handleUploadClick} disabled={uploading}>
          <Upload className="mr-2 h-4 w-4" />
          {uploading ? uploadStatus || 'Uploading...' : 'Upload File'}
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.txt,.md"
        />
      </div>

      <div className="flex items-center py-4">
        <Input
          placeholder="Filter files..."
          value={(table.getColumn('displayName')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('displayName')?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No files found. Upload a file to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {currentPager?.hasNextPage() && (
        <div className="flex items-center justify-center py-4">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}

      <div className="flex items-center justify-end py-2">
        <div className="text-sm text-muted-foreground">
          {files.length} file(s) loaded
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{fileToDelete?.displayName || fileToDelete?.name}"? This
              action cannot be undone and will remove the file from Gemini File Search.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RAGPage;
